/**
 * PageTabs 保活出口：按 pathname 缓存子页面，切换页签时隐藏而非卸载，以保留筛选、滚动、表单等状态。
 * 冻结 Route/Location 上下文，避免同构路由（如 /conf/conferences/:id/signup）之间 params 串台。
 */
import './KeepAliveOutlet.css';

import { useContext, useLayoutEffect, useRef } from 'react';
import { UNSAFE_LocationContext, UNSAFE_RouteContext, useLocation, useOutlet } from 'react-router-dom';
import { dispatchTableScrollYRemeasure } from '@/hooks/useTableScrollY';
import { KeepAlivePaneContext } from './keepAlivePaneContext';

function readMainScrollTop() {
    const main = document.querySelector('main.main-container');
    return main ? main.scrollTop : 0;
}

function writeMainScrollTop(top) {
    const main = document.querySelector('main.main-container');
    if (main) main.scrollTop = top;
}

export default function KeepAliveOutlet({ aliveKeys = [] }) {
    const outlet = useOutlet();
    const location = useLocation();
    const routeContext = useContext(UNSAFE_RouteContext);
    const locationContext = useContext(UNSAFE_LocationContext);
    const cacheRef = useRef(new Map());
    const scrollTopRef = useRef(new Map());
    const prevPathRef = useRef(location.pathname);

    const pathname = location.pathname;
    const cache = cacheRef.current;
    const keep = new Set(aliveKeys);
    keep.add(pathname);

    if (outlet && !cache.has(pathname)) {
        cache.set(pathname, {
            element: outlet,
            routeContext,
            locationContext,
        });
    }

    const activeEntry = cache.get(pathname);
    if (activeEntry) {
        activeEntry.routeContext = routeContext;
        activeEntry.locationContext = locationContext;
    }

    for (const key of [...cache.keys()]) {
        if (!keep.has(key)) {
            cache.delete(key);
            scrollTopRef.current.delete(key);
        }
    }

    const paneKeys = [...cache.keys()];

    useLayoutEffect(() => {
        const prev = prevPathRef.current;
        if (prev && prev !== pathname) {
            scrollTopRef.current.set(prev, readMainScrollTop());
        }
        writeMainScrollTop(scrollTopRef.current.get(pathname) ?? 0);
        prevPathRef.current = pathname;
        dispatchTableScrollYRemeasure();
    }, [pathname]);

    return (
        <div className="keep-alive-host">
            {paneKeys.map(key => {
                const cached = cache.get(key);
                if (!cached) return null;
                const active = key === pathname;
                return (
                    <div key={key} className={active ? 'keep-alive-pane keep-alive-pane--active' : 'keep-alive-pane keep-alive-pane--hidden'} data-keep-alive-key={key} aria-hidden={!active}>
                        <KeepAlivePaneContext.Provider value={active}>
                            <UNSAFE_LocationContext.Provider value={cached.locationContext}>
                                <UNSAFE_RouteContext.Provider value={cached.routeContext}>{cached.element}</UNSAFE_RouteContext.Provider>
                            </UNSAFE_LocationContext.Provider>
                        </KeepAlivePaneContext.Provider>
                    </div>
                );
            })}
        </div>
    );
}
